import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Loader2,
  Sparkles,
  CheckCircle,
  XCircle,
  Download,
  Copy,
  Play,
  Pause,
  RotateCcw,
  Image as ImageIcon,
  Car,
  Home,
  Wrench,
  Store
} from 'lucide-react';
import { ProductImageService } from '@/services/marketplace/ProductImageService';

interface ImageGenerationResult {
  id: string;
  prompt: string;
  status: 'pending' | 'generating' | 'completed' | 'failed';
  imageUrl?: string;
  error?: string;
  timestamp: Date;
}

// Physical product prompt templates
const PROMPT_TEMPLATES = {
  automotive: [
    "Modern car dealership showroom, professional salesperson showing QR code sticker on luxury SUV dashboard to young couple, smartphone displaying voice assistant interface, bright showroom lighting, glass windows, clean commercial photography, technology integration, 4K quality",
    "Auto repair service center, mechanic scanning QR code on car engine with smartphone, digital diagnostic overlay effects, professional garage setting, LED work lights, tools visible in background, commercial photography style",
    "Electric vehicle charging station, driver using smartphone to scan QR code on charging unit, modern parking garage, green LED status lights, evening lighting, urban environment, technology lifestyle photography",
    "Car interior close-up, hand pointing to QR code sticker on infotainment screen, dashboard visible, voice command visualization, soft ambient lighting, premium vehicle interior, shallow depth of field",
    "Roadside setting, person scanning QR code on car manual with smartphone, vehicle hood open in background, natural daylight, assistance scenario, realistic lifestyle photography"
  ],
  appliances: [
    "Modern kitchen, homeowner scanning QR code on stainless steel refrigerator with smartphone, voice wave visualization overlay, bright morning light, marble countertops, upscale home interior",
    "Laundry room, person troubleshooting washing machine using smartphone to scan QR code on control panel, LED display visible, clean modern home, natural window light, lifestyle photography",
    "Smart home living room, technician installing smart thermostat with QR code visible, smartphone showing setup interface, contemporary interior, professional installation scene, afternoon lighting",
    "Kitchen close-up, hand scanning QR code on dishwasher door with smartphone, digital interface overlay, modern appliances, clean minimalist design, commercial product photography",
    "Home theater setup, person scanning QR code on TV mount box, entertainment center visible, smartphone showing installation guide, modern living room, evening ambient lighting"
  ],
  retail: [
    "Electronics retail store, customer scanning QR code on product display with smartphone, store associate nearby, bright commercial lighting, technology products on shelves, retail environment",
    "Hardware store aisle, customer examining power tool with visible QR code label, smartphone in hand, industrial shelving, fluorescent lighting, commercial retail photography",
    "Appliance showroom, couple looking at washing machines with prominent QR codes, tablets showing product information, modern retail space, bright LED lighting, lifestyle shopping scene",
    "Furniture store, customer scanning QR code on sofa tag with smartphone, showroom setting, natural lighting, upscale retail environment, commercial photography",
    "Big box store, employee demonstrating QR code scanning on large appliance to customer, warehouse retail setting, industrial lighting, diverse subjects, commercial scene"
  ],
  industrial: [
    "Industrial warehouse, worker scanning QR code on forklift with rugged smartphone, safety equipment visible, warehouse lighting, professional environment, commercial photography",
    "Commercial kitchen, chef scanning QR code on industrial oven with smartphone, stainless steel surfaces, bright fluorescent lighting, professional cooking environment",
    "Office building mechanical room, HVAC technician scanning QR code on air handling unit, tablet showing diagnostics, industrial setting, LED work lights",
    "Manufacturing floor, engineer scanning QR code on industrial equipment control panel, factory setting, safety signage visible, professional industrial photography",
    "Construction site, contractor scanning QR code on heavy machinery with smartphone, outdoor setting, hard hat visible, morning light, commercial construction photography"
  ],
  service: [
    "Service van interior, technician preparing tablet after scanning equipment QR code, tools organized, mobile workspace, natural light through windows, professional service photography",
    "Residential driveway, HVAC technician scanning QR code on outdoor AC unit with smartphone, house exterior visible, sunny day, professional service call scene",
    "Apartment maintenance room, building manager scanning QR code on water heater with tablet, utility setting, fluorescent lighting, professional maintenance scenario",
    "Home basement, plumber scanning QR code on water filtration system, smartphone flashlight on, residential utility space, working conditions photography",
    "Attic space, installer scanning QR code on smart home hub, installation in progress, confined space, LED headlamp lighting, technical installation scene"
  ]
};

interface StorageImage {
  name: string;
  url: string;
  created_at: string;
  size?: number;
}

export default function ImageGeneratorAdmin() {
  const [results, setResults] = useState<ImageGenerationResult[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<keyof typeof PROMPT_TEMPLATES>('automotive');
  const [customPrompts, setCustomPrompts] = useState('');
  const [useCustomPrompts, setUseCustomPrompts] = useState(false);
  const [historyImages, setHistoryImages] = useState<StorageImage[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [activeTab, setActiveTab] = useState<'generator' | 'history'>('generator');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalImages, setTotalImages] = useState(0);
  const [selectedAspectRatio, setSelectedAspectRatio] = useState('16:9');
  const [selectedModel, setSelectedModel] = useState<'nano-banana' | 'imagen-4' | 'imagen-4-fast'>('nano-banana');
  const imagesPerPage = 20;

  // Available AI models
  const aiModels = [
    {
      value: 'nano-banana' as const,
      label: 'Nano Banana',
      description: 'Fast, branded style (with Help Genie reference images)',
      speed: 'Fast (~10-20s)',
      quality: 'Good'
    },
    {
      value: 'imagen-4' as const,
      label: 'Google Imagen 4',
      description: 'High quality, versatile styles',
      speed: 'Medium (~20-30s)',
      quality: 'Excellent'
    },
    {
      value: 'imagen-4-fast' as const,
      label: 'Imagen 4 Fast',
      description: 'Faster Imagen with good quality',
      speed: 'Fast (~10-15s)',
      quality: 'Very Good'
    }
  ];

  // Common aspect ratios for product photography
  const aspectRatios = [
    { value: '1:1', label: 'Square (1:1)', description: 'Instagram, product thumbnails' },
    { value: '4:3', label: 'Standard (4:3)', description: 'Traditional photo' },
    { value: '3:2', label: 'Classic (3:2)', description: 'DSLR standard' },
    { value: '16:9', label: 'Widescreen (16:9)', description: 'Web banners, hero images' },
    { value: '21:9', label: 'Ultrawide (21:9)', description: 'Cinematic, headers' },
    { value: '9:16', label: 'Portrait (9:16)', description: 'Mobile, stories' },
    { value: '2:3', label: 'Tall (2:3)', description: 'Portrait photos' },
    { value: '3:4', label: 'Vertical (3:4)', description: 'Product displays' },
  ];

  const handleGenerateBatch = async () => {
    const prompts = useCustomPrompts
      ? customPrompts.split('\n').filter(p => p.trim())
      : PROMPT_TEMPLATES[selectedCategory];

    if (prompts.length === 0) {
      alert('Please add prompts or select a category');
      return;
    }

    setIsGenerating(true);
    setIsPaused(false);
    setCurrentIndex(0);

    // Initialize results
    const initialResults: ImageGenerationResult[] = prompts.map((prompt, index) => ({
      id: `img-${Date.now()}-${index}`,
      prompt,
      status: 'pending',
      timestamp: new Date()
    }));
    setResults(initialResults);

    // Process prompts sequentially
    for (let i = 0; i < prompts.length; i++) {
      if (isPaused) {
        break;
      }

      setCurrentIndex(i);

      // Update status to generating
      setResults(prev => prev.map((r, idx) =>
        idx === i ? { ...r, status: 'generating' } : r
      ));

      try {
        const result = await ProductImageService.generateProductImage({
          product_name: `Product ${i + 1}`,
          custom_prompt: prompts[i],
          aspect_ratio: selectedAspectRatio,
          model: selectedModel
        });

        // Update with success
        setResults(prev => prev.map((r, idx) =>
          idx === i ? {
            ...r,
            status: 'completed',
            imageUrl: result.image_url
          } : r
        ));

      } catch (error) {
        // Update with error
        setResults(prev => prev.map((r, idx) =>
          idx === i ? {
            ...r,
            status: 'failed',
            error: error instanceof Error ? error.message : 'Generation failed'
          } : r
        ));
      }

      // Wait 3 seconds between generations to avoid rate limiting
      if (i < prompts.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 3000));
      }
    }

    setIsGenerating(false);

    // Refresh history after generation completes
    loadHistory();
  };

  const handlePauseResume = () => {
    setIsPaused(!isPaused);
  };

  const handleReset = () => {
    setResults([]);
    setIsGenerating(false);
    setIsPaused(false);
    setCurrentIndex(0);
  };

  const downloadAllImages = () => {
    results.forEach(result => {
      if (result.imageUrl) {
        window.open(result.imageUrl, '_blank');
      }
    });
  };

  const copyAllUrls = () => {
    const urls = results
      .filter(r => r.imageUrl)
      .map(r => r.imageUrl)
      .join('\n');
    navigator.clipboard.writeText(urls);
  };

  const completedCount = results.filter(r => r.status === 'completed').length;
  const failedCount = results.filter(r => r.status === 'failed').length;

  // Load history when switching to history tab or changing page
  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
  }, [activeTab, currentPage]);

  const loadHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const offset = (currentPage - 1) * imagesPerPage;

      // First get total count
      const { data: allFiles, error: countError } = await supabase.storage
        .from('marketplace-images')
        .list('products', {
          limit: 1000,
          offset: 0
        });

      if (countError) {
        console.error('Error getting count:', countError);
        return;
      }

      setTotalImages(allFiles?.length || 0);

      // List files for current page
      const { data, error } = await supabase.storage
        .from('marketplace-images')
        .list('products', {
          limit: imagesPerPage,
          offset: offset,
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (error) {
        console.error('Error loading history:', error);
        return;
      }

      if (data) {
        // Get public URLs for each file
        const images: StorageImage[] = data.map(file => {
          const { data: { publicUrl } } = supabase.storage
            .from('marketplace-images')
            .getPublicUrl(`products/${file.name}`);

          return {
            name: file.name,
            url: publicUrl,
            created_at: file.created_at || '',
            size: file.metadata?.size
          };
        });

        setHistoryImages(images);
      }
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const totalPages = Math.ceil(totalImages / imagesPerPage);

  return (
    <div className="container mx-auto p-6 max-w-7xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Image Generator Studio</h1>
          <p className="text-gray-600">Generate professional product imagery with Help Genie integration</p>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => {
          setActiveTab(v as 'generator' | 'history');
          if (v === 'history') {
            setCurrentPage(1); // Reset to first page when switching to history
          }
        }}>
          <TabsList className="mb-6">
            <TabsTrigger value="generator">
              <Sparkles className="mr-2 h-4 w-4" />
              Generator
            </TabsTrigger>
            <TabsTrigger value="history">
              <ImageIcon className="mr-2 h-4 w-4" />
              History ({totalImages})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="generator">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Prompt Selection */}
          <div className="lg:col-span-1">
            <Card className="h-full">
              <CardHeader>
                <CardTitle>Prompt Selection</CardTitle>
                <CardDescription>
                  Choose pre-built templates or create custom prompts
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs value={useCustomPrompts ? 'custom' : 'templates'}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="templates" onClick={() => setUseCustomPrompts(false)}>
                      Templates
                    </TabsTrigger>
                    <TabsTrigger value="custom" onClick={() => setUseCustomPrompts(true)}>
                      Custom
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="templates" className="space-y-4">
                    <div className="grid grid-cols-2 gap-2">
                      {Object.keys(PROMPT_TEMPLATES).map(category => (
                        <Button
                          key={category}
                          variant={selectedCategory === category ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setSelectedCategory(category as keyof typeof PROMPT_TEMPLATES)}
                          className="justify-start"
                        >
                          {category === 'automotive' && <Car className="mr-2 h-4 w-4" />}
                          {category === 'appliances' && <Home className="mr-2 h-4 w-4" />}
                          {category === 'retail' && <Store className="mr-2 h-4 w-4" />}
                          {category === 'industrial' && <Wrench className="mr-2 h-4 w-4" />}
                          {category === 'service' && <Wrench className="mr-2 h-4 w-4" />}
                          {category.charAt(0).toUpperCase() + category.slice(1)}
                        </Button>
                      ))}
                    </div>

                    <div className="space-y-2">
                      <p className="text-sm font-medium">
                        Selected: {selectedCategory} ({PROMPT_TEMPLATES[selectedCategory].length} prompts)
                      </p>
                      <ScrollArea className="h-64 w-full rounded border p-3">
                        <div className="space-y-2">
                          {PROMPT_TEMPLATES[selectedCategory].map((prompt, idx) => (
                            <div key={idx} className="text-xs text-gray-600 pb-2 border-b">
                              {idx + 1}. {prompt.substring(0, 100)}...
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </div>
                  </TabsContent>

                  <TabsContent value="custom" className="space-y-4">
                    <Textarea
                      placeholder="Enter prompts (one per line)..."
                      value={customPrompts}
                      onChange={(e) => setCustomPrompts(e.target.value)}
                      rows={12}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500">
                      {customPrompts.split('\n').filter(p => p.trim()).length} prompts entered
                    </p>
                  </TabsContent>
                </Tabs>

                {/* Model Selection */}
                <div className="mt-4 space-y-2">
                  <label className="text-sm font-medium block">AI Model</label>
                  <Select value={selectedModel} onValueChange={(v) => setSelectedModel(v as typeof selectedModel)}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select AI model" />
                    </SelectTrigger>
                    <SelectContent>
                      {aiModels.map((model) => (
                        <SelectItem key={model.value} value={model.value}>
                          <div className="py-1">
                            <div className="font-medium">{model.label}</div>
                            <div className="text-xs text-gray-500">{model.description}</div>
                            <div className="text-xs text-gray-400 flex gap-3 mt-1">
                              <span>⚡ {model.speed}</span>
                              <span>✨ {model.quality}</span>
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Aspect Ratio Selection */}
                <div className="mt-4 space-y-3">
                  <label className="text-sm font-medium block">Image Size</label>
                  <Select value={selectedAspectRatio} onValueChange={setSelectedAspectRatio}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select aspect ratio" />
                    </SelectTrigger>
                    <SelectContent>
                      {aspectRatios.map((ratio) => (
                        <SelectItem key={ratio.value} value={ratio.value}>
                          <div className="flex items-center gap-3">
                            <div
                              className="border-2 border-gray-400 bg-gray-200"
                              style={{
                                width: ratio.value === '1:1' ? '20px' :
                                       ratio.value === '16:9' ? '32px' :
                                       ratio.value === '21:9' ? '42px' :
                                       ratio.value === '9:16' ? '11px' :
                                       ratio.value === '4:3' ? '20px' :
                                       ratio.value === '3:2' ? '24px' :
                                       ratio.value === '2:3' ? '13px' :
                                       ratio.value === '3:4' ? '15px' : '24px',
                                height: ratio.value === '1:1' ? '20px' :
                                        ratio.value === '16:9' ? '18px' :
                                        ratio.value === '21:9' ? '18px' :
                                        ratio.value === '9:16' ? '20px' :
                                        ratio.value === '4:3' ? '15px' :
                                        ratio.value === '3:2' ? '16px' :
                                        ratio.value === '2:3' ? '20px' :
                                        ratio.value === '3:4' ? '20px' : '16px'
                              }}
                            />
                            <div>
                              <span className="font-medium">{ratio.label}</span>
                              <span className="text-xs text-gray-500 ml-2">
                                {ratio.description}
                              </span>
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Visual Preview of Selected Aspect Ratio */}
                  <div className="flex items-center justify-center p-4 bg-gray-50 rounded">
                    <div
                      className="border-2 border-purple-500 bg-purple-100"
                      style={{
                        width: selectedAspectRatio === '1:1' ? '80px' :
                               selectedAspectRatio === '16:9' ? '128px' :
                               selectedAspectRatio === '21:9' ? '168px' :
                               selectedAspectRatio === '9:16' ? '45px' :
                               selectedAspectRatio === '4:3' ? '80px' :
                               selectedAspectRatio === '3:2' ? '96px' :
                               selectedAspectRatio === '2:3' ? '53px' :
                               selectedAspectRatio === '3:4' ? '60px' : '96px',
                        height: selectedAspectRatio === '1:1' ? '80px' :
                                selectedAspectRatio === '16:9' ? '72px' :
                                selectedAspectRatio === '21:9' ? '72px' :
                                selectedAspectRatio === '9:16' ? '80px' :
                                selectedAspectRatio === '4:3' ? '60px' :
                                selectedAspectRatio === '3:2' ? '64px' :
                                selectedAspectRatio === '2:3' ? '80px' :
                                selectedAspectRatio === '3:4' ? '80px' : '64px'
                      }}
                    />
                  </div>
                </div>

                {/* Generation Controls */}
                <div className="mt-6 space-y-3">
                  <Button
                    onClick={handleGenerateBatch}
                    disabled={isGenerating}
                    className="w-full"
                    size="lg"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Generating ({currentIndex + 1}/{results.length})
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-2 h-4 w-4" />
                        Generate Images
                      </>
                    )}
                  </Button>

                  {isGenerating && (
                    <Button
                      onClick={handlePauseResume}
                      variant="outline"
                      className="w-full"
                    >
                      {isPaused ? (
                        <>
                          <Play className="mr-2 h-4 w-4" />
                          Resume
                        </>
                      ) : (
                        <>
                          <Pause className="mr-2 h-4 w-4" />
                          Pause
                        </>
                      )}
                    </Button>
                  )}

                  {results.length > 0 && (
                    <Button
                      onClick={handleReset}
                      variant="outline"
                      className="w-full"
                    >
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Reset
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Results */}
          <div className="lg:col-span-2">
            <Card className="h-full">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Generation Results</CardTitle>
                    <CardDescription>
                      {results.length > 0 && (
                        <div className="flex gap-4 mt-2">
                          <Badge variant="default">
                            <CheckCircle className="mr-1 h-3 w-3" />
                            {completedCount} Completed
                          </Badge>
                          {failedCount > 0 && (
                            <Badge variant="destructive">
                              <XCircle className="mr-1 h-3 w-3" />
                              {failedCount} Failed
                            </Badge>
                          )}
                          {isGenerating && (
                            <Badge variant="secondary">
                              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                              Generating...
                            </Badge>
                          )}
                        </div>
                      )}
                    </CardDescription>
                  </div>
                  {completedCount > 0 && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={copyAllUrls}
                      >
                        <Copy className="mr-1 h-3 w-3" />
                        Copy URLs
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={downloadAllImages}
                      >
                        <Download className="mr-1 h-3 w-3" />
                        Download All
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {results.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <ImageIcon className="mx-auto h-12 w-12 mb-4 opacity-50" />
                    <p>No images generated yet</p>
                    <p className="text-sm mt-2">Select prompts and click Generate to start</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[600px] pr-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {results.map((result) => (
                        <div
                          key={result.id}
                          className={`border rounded-lg p-3 ${
                            result.status === 'generating' ? 'border-blue-500 bg-blue-50' :
                            result.status === 'completed' ? 'border-green-500 bg-green-50' :
                            result.status === 'failed' ? 'border-red-500 bg-red-50' :
                            'border-gray-300'
                          }`}
                        >
                          <div className="space-y-2">
                            {/* Status Header */}
                            <div className="flex justify-between items-start">
                              <Badge
                                variant={
                                  result.status === 'completed' ? 'default' :
                                  result.status === 'failed' ? 'destructive' :
                                  result.status === 'generating' ? 'secondary' :
                                  'outline'
                                }
                              >
                                {result.status === 'generating' && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                                {result.status === 'completed' && <CheckCircle className="mr-1 h-3 w-3" />}
                                {result.status === 'failed' && <XCircle className="mr-1 h-3 w-3" />}
                                {result.status}
                              </Badge>
                              <span className="text-xs text-gray-500">
                                {result.timestamp.toLocaleTimeString()}
                              </span>
                            </div>

                            {/* Prompt */}
                            <p className="text-xs text-gray-600 line-clamp-2">
                              {result.prompt}
                            </p>

                            {/* Image or Error */}
                            {result.status === 'completed' && result.imageUrl && (
                              <div className="space-y-2">
                                <img
                                  src={result.imageUrl}
                                  alt="Generated"
                                  className="w-full rounded border"
                                />
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="flex-1"
                                    onClick={() => window.open(result.imageUrl, '_blank')}
                                  >
                                    <Download className="mr-1 h-3 w-3" />
                                    Open
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="flex-1"
                                    onClick={() => navigator.clipboard.writeText(result.imageUrl!)}
                                  >
                                    <Copy className="mr-1 h-3 w-3" />
                                    Copy
                                  </Button>
                                </div>
                              </div>
                            )}

                            {result.status === 'failed' && result.error && (
                              <Alert variant="destructive">
                                <AlertDescription className="text-xs">
                                  {result.error}
                                </AlertDescription>
                              </Alert>
                            )}

                            {result.status === 'generating' && (
                              <div className="h-32 bg-gray-100 rounded flex items-center justify-center">
                                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Image History</CardTitle>
                    <CardDescription>
                      Showing {historyImages.length} of {totalImages} images (Page {currentPage} of {totalPages})
                    </CardDescription>
                  </div>
                  <Button
                    onClick={loadHistory}
                    variant="outline"
                    size="sm"
                    disabled={isLoadingHistory}
                  >
                    {isLoadingHistory ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <RotateCcw className="mr-2 h-4 w-4" />
                        Refresh
                      </>
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {isLoadingHistory ? (
                  <div className="text-center py-12">
                    <Loader2 className="mx-auto h-8 w-8 animate-spin text-gray-400" />
                    <p className="mt-4 text-gray-500">Loading images...</p>
                  </div>
                ) : historyImages.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <ImageIcon className="mx-auto h-12 w-12 mb-4 opacity-50" />
                    <p>No images found in storage</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {historyImages.map((image) => (
                      <div
                        key={image.name}
                        className="border rounded-lg overflow-hidden group hover:shadow-lg transition-shadow"
                      >
                        <div className="aspect-video bg-gray-100 relative">
                          <img
                            src={image.url}
                            alt={image.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                        <div className="p-3 space-y-2">
                          <p className="text-xs text-gray-600 truncate" title={image.name}>
                            {image.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(image.created_at).toLocaleDateString()} {new Date(image.created_at).toLocaleTimeString()}
                          </p>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1"
                              onClick={() => window.open(image.url, '_blank')}
                            >
                              <Download className="mr-1 h-3 w-3" />
                              Open
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1"
                              onClick={() => navigator.clipboard.writeText(image.url)}
                            >
                              <Copy className="mr-1 h-3 w-3" />
                              Copy
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-6 pb-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    >
                      First
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }

                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(pageNum)}
                            className="min-w-[40px]"
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                    >
                      Last
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
    </div>
  );
}